import React, { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { useDepartments } from "../../../hooks/useDepartments";
import { projectService } from "../../../services/projectService";
import { departmentSubmissionService } from "../../../services/departmentSubmissionService";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import Button from "../../ui/Button";
import LoadingSpinner from "../../common/LoadingSpinner";
import {
  SECTORS,
  USER_ROLES,
  getSectorLabel,
} from "../../../constants/authConstants";
import {
  DEFAULT_PROJECT_DATA,
  formatFinancialRequirement,
} from "../../../constants/projectConstants";
import { validateProjectForm } from "../../../utils/projectValidation";
import { financialYearUtils } from "../../../utils/helpers";

const PlanningForm = () => {
  const { user, getUserRole } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const {
    departments,
    loading: loadingDepartments,
    getDepartmentLabel,
    getDepartmentOptions,
  } = useDepartments();

  const isAdmin = getUserRole() === USER_ROLES.ADMIN;

  const [formData, setFormData] = useState(() => ({
    ...DEFAULT_PROJECT_DATA,
    sector: isAdmin ? "" : user?.sector || "",
    department: isAdmin ? "" : user?.department || "",
  }));

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  // User's pending projects
  const [userProjects, setUserProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [deletingProject, setDeletingProject] = useState(null);

  // Submission status
  const [currentSubmission, setCurrentSubmission] = useState(null);
  const [loadingSubmission, setLoadingSubmission] = useState(false);

  const containerPadding = getResponsiveValue({
    mobile: "p-4",
    desktop: "p-6",
  });

  const gridCols2 = getResponsiveValue({
    mobile: "grid-cols-1",
    desktop: "grid-cols-2",
  });

  const gridGap = getResponsiveValue({
    mobile: "gap-4",
    desktop: "gap-6",
  });

  const titleSize = getResponsiveValue({
    mobile: "text-xl",
    desktop: "text-2xl",
  });

  const currentFinancialYear = financialYearUtils.getCurrentFinancialYear();
  const financialYearDescription =
    financialYearUtils.getFinancialYearDescription(currentFinancialYear);

  // Check if form is locked
  const isFormLocked = currentSubmission?.isLocked || false;

  // Load data on mount
  useEffect(() => {
    loadUserProjects();
    if (!isAdmin && user?.department) {
      loadCurrentSubmission();
    }
  }, [user, isAdmin]);

  const loadUserProjects = async () => {
    try {
      setLoadingProjects(true);
      const result = await projectService.getProjectsByUser(user.uid);
      if (result.success) {
        const pendingProjects = result.data.filter(
          (p) => p.status === "pending"
        );
        setUserProjects(pendingProjects);
      }
    } catch (error) {
      console.error("Error loading user projects:", error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadCurrentSubmission = async () => {
    try {
      setLoadingSubmission(true);
      const currentFY = financialYearUtils.getCurrentFinancialYear();
      const submission =
        await departmentSubmissionService.getSubmissionByDepartment(
          user.department,
          currentFY
        );
      setCurrentSubmission(submission);
    } catch (error) {
      console.error("Error loading submission:", error);
    } finally {
      setLoadingSubmission(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    if (success) setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if locked
    if (isFormLocked && !isAdmin) {
      alert("Your submission is locked. You cannot add new projects. ");
      return;
    }

    const validation = validateProjectForm(formData, isAdmin);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      const result = await projectService.submitProject(formData, user.uid);

      if (result.success) {
        setSuccess(true);
        setSubmitMessage(
          "Project submitted successfully! It is now pending approval."
        );

        setFormData({
          ...DEFAULT_PROJECT_DATA,
          sector: isAdmin ? "" : user?.sector || "",
          department: isAdmin ? "" : user?.department || "",
          negotiatedLoan: "",
          loanParty: "",
        });

        loadUserProjects();

        setTimeout(() => {
          setSuccess(false);
          setSubmitMessage("");
        }, 5000);
      }
    } catch (error) {
      console.error("Error submitting project:", error);
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (
      !confirm(
        `Are you sure you want to delete "${projectName}"?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setDeletingProject(projectId);
      const result = await projectService.deleteProject(projectId);

      if (result.success) {
        setUserProjects((prev) => prev.filter((p) => p.id !== projectId));
        setSuccess(true);
        setSubmitMessage("Project deleted successfully!");
        setTimeout(() => {
          setSuccess(false);
          setSubmitMessage("");
        }, 3000);
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      setErrors({ general: error.message });
    } finally {
      setDeletingProject(null);
    }
  };

  const getAvailableDepartments = () => {
    if (isAdmin) {
      if (formData.sector) {
        return getDepartmentOptions(formData.sector);
      }
      return [];
    } else {
      return [
        {
          value: formData.department,
          label: getDepartmentLabel(formData.department),
        },
      ];
    }
  };

  const sectorOptions = SECTORS.map((sector) => ({
    value: sector.value,
    label: sector.label,
  }));

  if (loadingDepartments) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading form..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {success && (
        <div className="bg-success-subtle border border-border-primary rounded-xl p-3">
          <div className="flex items-center">
            <svg
              className="w-4 h-4 text-success mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-success font-medium text-sm">
              {submitMessage}
            </span>
          </div>
        </div>
      )}

      {/* Locked Banner */}
      {!loadingSubmission && isFormLocked && !isAdmin && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-orange-600 mr-3"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-orange-800">
                Submission Locked - Cannot Add Projects
              </p>
              <p className="text-xs text-orange-600 mt-1">
                Your project submission is locked.
              </p>
            </div>
          </div>
        </div>
      )}

      <div>
        <h1 className={`${titleSize} font-bold text-text-primary mb-1`}>
          New Project Proposal
        </h1>
        <p className="text-text-secondary text-sm">
          {isAdmin
            ? "Submit a new project proposal for any department"
            : "Submit a new project proposal for your department"}
        </p>
        <p className="text-text-tertiary text-xs mt-1">
          This project will be assigned to the next financial year:{" "}
          <span className="font-medium">{financialYearDescription}</span>
        </p>
      </div>

      <div>
        <form
          onSubmit={handleSubmit}
          className={`space-y-4 ${
            isFormLocked && !isAdmin ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          {errors.general && (
            <div className="bg-error-subtle border border-border-error rounded-xl p-3">
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 text-error mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-error text-sm">{errors.general}</span>
              </div>
            </div>
          )}

          <div className={`grid ${gridCols2} ${gridGap}`}>
            <div>
              {isAdmin ? (
                <Select
                  label="Sector"
                  value={formData.sector}
                  onChange={(e) => handleInputChange("sector", e.target.value)}
                  options={sectorOptions}
                  error={errors.sector}
                  required
                  placeholder="Enter a sector"
                />
              ) : (
                <div>
                  <label className="text-sm font-medium text-text-primary mb-1 block">
                    Sector <span className="text-error">*</span>
                  </label>
                  <div className="px-3 py-2 bg-surface-secondary rounded-xl border border-border-primary text-text-primary text-sm">
                    {getSectorLabel(formData.sector) || "Not assigned"}
                  </div>
                </div>
              )}
            </div>

            <div>
              {isAdmin ? (
                <Select
                  label="Department"
                  value={formData.department}
                  onChange={(e) =>
                    handleInputChange("department", e.target.value)
                  }
                  options={getAvailableDepartments()}
                  error={errors.department}
                  required
                  placeholder="Enter the department"
                  disabled={!formData.sector || loadingDepartments}
                />
              ) : (
                <div>
                  <label className="text-sm font-medium text-text-primary mb-1 block">
                    Department <span className="text-error">*</span>
                  </label>
                  <div className="px-3 py-2 bg-surface-secondary rounded-xl border border-border-primary text-text-primary text-sm">
                    {getDepartmentLabel(formData.department) || "Not assigned"}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={`grid ${gridCols2} ${gridGap}`}>
            <Input
              label="Name of Project"
              type="text"
              value={formData.projectName}
              onChange={(e) => handleInputChange("projectName", e.target.value)}
              placeholder="Enter the project name"
              error={errors.projectName}
              required
            />

            <Input
              label="Total Estimate Cost (in lakh)"
              type="number"
              value={formData.financialRequirement}
              onChange={(e) =>
                handleInputChange("financialRequirement", e.target.value)
              }
              placeholder="Enter amount in lakhs"
              error={errors.financialRequirement}
              required
              icon={<span className="text-gray-400 font-semibold">₹</span>}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-text-primary mb-1 block">
              Brief Description <span className="text-error">*</span>
            </label>
            <textarea
              value={formData.brief}
              onChange={(e) => handleInputChange("brief", e.target.value)}
              placeholder="Provide a detailed description of the project, its objectives, and expected outcomes..."
              rows={8}
              className={`
                w-full border border-border-primary rounded-xl bg-surface-primary
                focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-border-focus
                text-text-primary placeholder:text-text-tertiary
                transition-colors duration-200 px-3 py-2 text-sm
                ${
                  errors.brief
                    ? "border-border-error focus:ring-border-error focus:border-border-error"
                    : ""
                }
              `}
            />
            {errors.brief && (
              <span className="text-sm text-error flex items-center mt-1">
                <svg
                  className="w-4 h-4 mr-1 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {errors.brief}
              </span>
            )}
          </div>

          <div className="flex justify-end pt-2 border-t border-border-primary">
            <Button
              type="submit"
              loading={loading}
              disabled={
                loading || loadingDepartments || (isFormLocked && !isAdmin)
              }
              size="md"
              className={isMobile ? "w-full" : ""}
            >
              {loading ? "Submitting..." : "Submit Project"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanningForm;